import { t } from 'localization'
import { OfferModalDetails } from 'offers/types'
import { formatPrice } from 'shared/components/ProductGrid/__utils__/formatPrice'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import { PriceLocalized } from 'shared/types/PriceLocalized'

// Note: These should be coming from the BE but still awaiting endpoint
const shortCutPercentages = [
  { display: '5% off', percentageValue: 0.05 },
  { display: '10% off', percentageValue: 0.1 },
  { display: '15% off', percentageValue: 0.15 },
]

// Note: These should be coming from the BE but still awaiting endpoint
export const expirationDaysOptions = [3, 7, 14, 30]

const TP = 'offers.components.OffersModalContents'

export const getShortcutValues = (offerModalDetails: OfferModalDetails | null) => {
  const minOfferCents = offerModalDetails?.minimumOfferCents || 2500 // $25 USD
  const value = offerModalDetails?.lowestPriceCents?.value || 0
  const isTooLow = shortCutPercentages.some(
    (shortcut) => value * (1 - shortcut.percentageValue) < minOfferCents,
  )

  if (isTooLow) {
    return null
  } else {
    return shortCutPercentages.map((shortcut) => {
      const display = formatMoneyObject(
        {
          currency: 'USD',
          amount: value - value * shortcut.percentageValue,
          amountUsdCents: value - value * shortcut.percentageValue,
        },
        { showCents: false },
      )
      return {
        display,
        value: value - value * shortcut.percentageValue,
        percentageDisplay: shortcut.display,
        percentageValue: shortcut.percentageValue,
      }
    })
  }
}

export const getNumberValue = (string: string = '0') => {
  const strippedValue = string.replace(/[^0-9]/g, '')
  return parseInt(strippedValue)
}

export const getExpirationDate = (expirationValue: number) => {
  const date = new Date()
  date.setDate(date.getDate() + expirationValue)

  const formatData = (input: number) => {
    if (input > 9) {
      return input
    }
    return `0${input}`
  }

  const dd = formatData(date.getDate())
  const mm = formatData(date.getMonth() + 1)
  const yyyy = date.getFullYear()

  const time = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })

  return `Expires ${mm}/${dd}/${yyyy} - ${time}`
}

export const getRemainingOfferTime = (offer: { activatedAt: string; offerDuration: number }) => {
  const activatedAt = new Date(offer.activatedAt)
  const expirationDate = new Date(activatedAt.getTime() + offer.offerDuration * 60 * 60 * 1000)
  const currentDate = new Date()
  const remainingTime = expirationDate.getTime() - currentDate.getTime()

  const d = remainingTime / 1000 / 60 / 60 / 24
  const h = (remainingTime / 1000 / 60 / 60) % 24
  const m = (remainingTime / 1000 / 60) % 60

  const days = remainingTime < 0 ? Math.ceil(d) : Math.floor(d)
  const hours = remainingTime < 0 ? Math.ceil(h) : Math.floor(h)
  const minutes = remainingTime < 0 ? Math.ceil(m) : Math.floor(m)

  return {
    days,
    hours,
    minutes,
    display: `${days}D ${hours}H ${minutes}M`,
    isLessThanADay: days === 0 && hours >= 0 && minutes > 0,
    expired: remainingTime < 0,
  }
}

export const getSizingLabel = (offerModalDetails: OfferModalDetails | null) => {
  if (!offerModalDetails) return ''

  const sizeCategory = offerModalDetails.sizeCategory?.charAt(0).toUpperCase() || ''
  return `US ${sizeCategory} ${offerModalDetails?.size?.display} (New)`
}

export const getConversionPrice = ({
  offerValue,
  isoCode,
  symbol,
  rate,
}: {
  offerValue: string
  isoCode: string
  symbol: string
  rate: number
}): { display: string; price: PriceLocalized } => {
  const value = Math.ceil(getNumberValue(offerValue) * parseFloat(rate.toFixed(4))) * 100
  const conversionPrice = formatPrice(value, isoCode, symbol)

  if (!conversionPrice || conversionPrice === 'Out of stock') {
    return {
      display: '',
      price: {
        amount: 0,
        currency: isoCode,
        amountUsdCents: 0,
      } as PriceLocalized,
    }
  }

  return {
    display: conversionPrice,
    price: {
      amount: value,
      currency: isoCode,
      amountUsdCents: getNumberValue(offerValue),
    } as PriceLocalized,
  }
}

export const getOfferModalButtonText = ({
  offerValue,
  offerExceedsLowestPrice,
  operation,
}: {
  offerValue: string
  offerExceedsLowestPrice: boolean
  operation: 'edit' | 'create' | 'buynew'
}) => {
  const currentOffer = getNumberValue(offerValue)
  const offerAboveZero = currentOffer > 0

  // Handle buynow operation first.
  if (operation === 'buynew') {
    return `${t(`${TP}.buttonTextBuyNew`, 'Buy new')} - ${offerValue}`
  }

  // Determine CTA text based on Offer operation ('edit' or 'create')
  const ctaOfferText =
    operation === 'edit'
      ? t(`${TP}.buttonTextEditOffer`, 'Update offer')
      : t(`${TP}.buttonTextPlaceOffer`, 'Place offer')

  // Append offer value if applicable
  return offerAboveZero && !offerExceedsLowestPrice
    ? `${ctaOfferText} - ${offerValue}`
    : ctaOfferText
}
